import { Capacitor } from '@capacitor/core';
import * as Sentry from '@sentry/react';

export type tMediaKind = 'audio' | 'image' | 'video';
export type tMediaEngine = 'howler' | 'html5-audio' | 'wavesurfer';

interface IBaseMediaErrorContext {
  /** Source URL that failed to load. */
  src?: string | null;
  /** Short label for where the media lives (e.g. 'picture', 'animated-icon'). */
  context?: string;
  /** Merged into the Sentry `extra` payload. Prefer this over encoding values into the
   *  Error message: the message drives grouping, so a value there splits one failure
   *  mode into one issue per distinct value. */
  extra?: Record<string, unknown>;
}

interface IAudioErrorContext extends IBaseMediaErrorContext {
  kind: 'audio';
  /** Playback engine. */
  engine?: tMediaEngine;
  /** Whether HTML5 (streaming/range) mode was active. */
  html5?: boolean;
  /** Whether this failure happened during the WebAudio fallback attempt. */
  fallback?: boolean;
}

interface IImageErrorContext extends IBaseMediaErrorContext {
  kind: 'image';
}

interface IVideoErrorContext extends IBaseMediaErrorContext {
  kind: 'video';
}

export type tCaptureMediaLoadErrorContext =
  | IAudioErrorContext
  | IImageErrorContext
  | IVideoErrorContext;

/** Report each failing (kind, asset) once per session: media `error` events fire repeatedly
 *  for a persistently broken source, and the same URL breaks across several components. */
const reportedKeys = new Set<string>();

/** Turn a MediaError / DOMException / Event / string into a titled Error so the
 *  Sentry issue title and grouping stay meaningful (MediaError.code: 1=aborted,
 *  2=network, 3=decode, 4=src-not-supported). */
function toReportableError(error: unknown): Error {
  if (error instanceof Error) return error;

  if (error && typeof error === 'object' && 'code' in error) {
    const { code, message } = error as { code?: unknown; message?: unknown };
    return new Error(
      `Media error (code ${String(code)})${message ? `: ${String(message)}` : ''}`
    );
  }

  return new Error(String(error));
}

function getOpaqueScheme(src: string): 'blob' | 'data' | null {
  if (src.startsWith('blob:')) return 'blob';
  if (src.startsWith('data:')) return 'data';
  return null;
}

function parseUrl(src: string): URL | null {
  try {
    // No `document` outside the DOM (SSR, workers) → a relative src cannot resolve.
    const base = typeof document === 'undefined' ? undefined : document.baseURI;
    return new URL(src, base);
  } catch {
    return null;
  }
}

/**
 * Presigned CDN URLs embed `X-Amz-Signature`/`X-Amz-Date`, so keying on the raw URL
 * re-reports the same broken asset on every refetch — hence `origin + pathname` only.
 * `blob:`/`data:` collapse into one bucket per scheme: a blob URL is a random per-session
 * handle, so keying on it would re-report every recording and grow the set without bound.
 */
function getAssetIdentity(src?: string | null): string {
  if (!src) return 'unknown';

  const opaqueScheme = getOpaqueScheme(src);
  if (opaqueScheme) return `${opaqueScheme}:`;

  const url = parseUrl(src);
  if (url) return `${url.origin}${url.pathname}`;

  return src.split(/[?#]/)[0];
}

/** Host the asset was served from, so per-CDN failure rates become queryable. */
function getMediaHost(src?: string | null): string {
  if (!src) return 'unknown';

  const opaqueScheme = getOpaqueScheme(src);
  if (opaqueScheme) return opaqueScheme;

  const url = parseUrl(src);
  if (!url) return 'unknown';
  // Resolved against our own document → same-origin asset, not a CDN failure.
  if (typeof window !== 'undefined' && url.origin === window.location.origin) {
    return 'relative';
  }

  return url.host;
}

interface IRuntimeDimensions {
  /** Runtime container ('ios' | 'android' | 'web') — NOT the build-time bundle tag. */
  platform: string;
  pointer: 'coarse' | 'fine' | 'unknown';
}

function getRuntimeDimensions(): IRuntimeDimensions {
  let platform = 'unknown';
  try {
    platform = Capacitor.getPlatform();
  } catch {
    // Capacitor bridge unavailable — leave 'unknown' rather than lose the report.
  }

  let pointer: IRuntimeDimensions['pointer'] = 'unknown';
  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    try {
      pointer = window.matchMedia('(pointer: coarse)').matches ? 'coarse' : 'fine';
    } catch {
      // Some WebViews throw on unsupported media features.
    }
  }

  return { platform, pointer };
}

interface INetworkConditions {
  online?: boolean;
  /** Data Saver: throttles/blocks media preloading, a candidate cause of "never ready". */
  saveData?: boolean;
  effectiveType?: string;
}

/** Connection hints where available — `navigator.connection` is Chromium-only. */
function getNetworkConditions(): INetworkConditions {
  if (typeof navigator === 'undefined') return {};

  const { connection } = navigator as Navigator & {
    connection?: { saveData?: boolean; effectiveType?: string };
  };

  return {
    online: navigator.onLine,
    saveData: connection?.saveData,
    effectiveType: connection?.effectiveType
  };
}

/** Single entry point for every media type, so the Sentry tag schema stays unified. */
export function captureMediaLoadError(
  error: unknown,
  context: tCaptureMediaLoadErrorContext
): void {
  const assetIdentity = getAssetIdentity(context.src);
  const fallbackPart = context.kind === 'audio' ? `:${context.fallback ?? false}` : '';
  /*
   * Keyed on the ASSET, not the call site: including `context` un-collapses report sites
   * that legitimately describe one broken asset (a Howler `loaderror`, the consumer's own
   * handler, a play rejection) and multiplies volume during a CDN incident, when we can
   * least afford rate-limiting. It stays queryable as the `media_context` tag instead.
   */
  const key = `${context.kind}:${assetIdentity}${fallbackPart}`;
  if (reportedKeys.has(key)) return;
  reportedKeys.add(key);

  const runtime = getRuntimeDimensions();
  const tags: Record<string, string> = {
    media_type: context.kind,
    media_host: getMediaHost(context.src),
    media_platform: runtime.platform,
    media_pointer: runtime.pointer
  };
  if (context.context) tags.media_context = context.context;
  if (context.kind === 'audio') {
    if (context.engine) tags.media_engine = context.engine;
    if (context.html5 !== undefined) tags.audio_html5 = String(context.html5);
    if (context.fallback !== undefined) tags.audio_fallback = String(context.fallback);
  }

  Sentry.captureException(toReportableError(error), {
    tags,
    extra: {
      src: context.src,
      mediaAsset: assetIdentity,
      platform: runtime.platform,
      pointer: runtime.pointer,
      ...getNetworkConditions(),
      rawError: error,
      // Call-site detail last: it is the most specific, so it wins any key collision.
      ...context.extra
    }
  });

  /*
   * Do NOT re-add the `fetch(src, { method: 'HEAD' })` status probe that used to live here:
   * it cost an extra request *and* a second Sentry event per failed asset (a CDN incident
   * amplified into both a traffic and an event multiplier), and it fabricated
   * `cors-or-network` for third-party embed hosts and iOS `capacitor://localhost` bundles.
   */
}
